import { useEffect } from "react";
import {
  createRootRouteWithContext,
  Outlet,
  useRouterState,
} from "@tanstack/react-router";
import { QueryClient, useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import "dayjs/locale/ar";
import durationPlugin from "dayjs/plugin/duration";
import localeData from "dayjs/plugin/localeData";
import { SnackbarProvider } from "@/app/lib/context/SnackbarContext";
import { FlightSearchDataProvider } from "@/app/lib/context/FlightSearchDataContext";
import { applicationStateQueryOptions } from "../app/lib/hooks/useApplicationStateTurnedOff";
import { FlightSortProvider } from "@/app/lib/context/FlightSortContext";
import { PaymentContextProvider } from "@/app/lib/context/PaymentContext";
import { UnderMaintenance } from "../app/ui/UnderMaintenance";
import { DefaultErrorComponent } from "@/app/ui/errors/DefaultErrorComponent";
import { platformApi } from "@/app/lib/platform";
import { HotelSearchDataProvider } from "@/features/hotels/contexts/HotelSearchDataContext";
import { HotelFiltersProvider } from "@/features/hotels/contexts/HotelFiltersContext";
import { HotelSortProvider } from "@/features/hotels/contexts/HotelSortContext";
import { HotelDataProvider } from "@/features/hotels/contexts/HotelDataContext";
import { HotelBookingContextProvider } from "@/features/hotels/contexts/HotelBookingContext";

dayjs.extend(durationPlugin);
dayjs.extend(localeData);

type HotelBookingStep = {
  stepNumber: number;
  presentationMode?: "modal";
};

declare module "@tanstack/react-router" {
  interface StaticDataRouteOption {
    hotelStep?: HotelBookingStep;
    drawer?: boolean;
  }
}

interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RouteComponent,
  errorComponent: DefaultErrorComponent,
});

function RouteComponent() {
  const router = useRouterState();

  const { data: underMaintenance } = useQuery(applicationStateQueryOptions());

  useEffect(() => {
    platformApi.setNavigationBar(
      router.location.pathname === "/"
        ? {
            backgroundColor: "#003E2B",
            image: `${location.origin}/tzlogo.png`,
          }
        : {
            backgroundColor: "#f5f6f5",
            image: null,
          },
    );
  }, [router.location.pathname]);

  return (
    <SnackbarProvider>
      {underMaintenance ? (
        <main className="h-dvh w-dvh">
          <UnderMaintenance />
        </main>
      ) : (
        // TODO: Move this to a specific route
        <FlightSearchDataProvider>
          <FlightSortProvider>
            <HotelSearchDataProvider>
              <HotelFiltersProvider>
                <HotelSortProvider>
                  <HotelDataProvider>
                    <HotelBookingContextProvider>
                      <PaymentContextProvider>
                        <Outlet />
                      </PaymentContextProvider>
                    </HotelBookingContextProvider>
                  </HotelDataProvider>
                </HotelSortProvider>
              </HotelFiltersProvider>
            </HotelSearchDataProvider>
          </FlightSortProvider>
        </FlightSearchDataProvider>
      )}
    </SnackbarProvider>
  );
}
